import { Injectable, OnDestroy } from '@angular/core';
import { HttpErrorResponse, HttpParams, HttpResponse, HttpClient, HttpContext } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, catchError, Observable, throwError, map, tap, of, switchMap } from 'rxjs';
import { User } from './model/tf-user';
import { RegisterModel } from './model/tf-register';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { PaginationService } from '../Theme/shared/components/pagination/pagination.service';
import { PaginationParams } from '../Theme/shared/components/pagination/pagination.interface';
import { NotificationService } from '../Theme/layout/admin/notification.service';
import { PortalLayoutResolveService } from '../admin/ad-admin/ad-layout/portal-layout-resolve.service';
import { CfHomeContentService } from '../customer/cf-customer/cf-home/service/cf-home-content.service';
import { ProductCardDisplayConfigService } from '../Theme/shared/components/product/product-card-display-config.service';
import { FilterLayoutConfigService } from '../Theme/shared/components/filter/filter-layout-config.service';
import { SKIP_SPINNER } from '../Theme/shared/service/spinner.service';
import { ShoppingBasketLayoutConfigService } from '../customer/cp-customer/cp-shopping-basket/service/shopping-basket-layout-config.service';
import { PORTAL_COMPONENT_KEYS, shouldLoadPortalLayoutsForUser } from '../admin/ad-admin/ad-layout/ad-layout.models';
import { AuthMethod } from '../Theme/shared/service/security-config/security-config.model';

export interface AuthInfo {
  isAuthenticated: boolean;
  username?: string;
  email?: string;
  isStaff?: boolean;
  userGroups?: string[];
  userId?: string;
  /** Set when Cognito requires MFA or new-password step (HTTP 200, not yet authenticated). */
  challenge?: string;
  session?: string;
  /** MFA_SETUP only: authenticator enrollment secret returned before cookies are set. */
  secretCode?: string;
  otpauthUrl?: string;
}

interface LoginSuccessResponse {
  isAuthenticated?: boolean;
  username: string;
  is_staff: boolean;
  /** Backend may send a single group name or a list. */
  groupName: string[] | string;
  id: string;
}

/** HTTP 200 — login in progress, another Cognito step required. */
interface LoginChallengeResponse {
  isAuthenticated: false;
  challenge: string;
  session: string;
  email?: string;
  secretCode?: string;
  otpauthUrl?: string | null;
}

type LoginApiResponse = LoginSuccessResponse | LoginChallengeResponse;

/** `?reason=` value the login page reads to explain why the session ended. */
export const AUTH_CONFIG_CHANGED_REASON = 'auth_config_changed';

/** Refresh rejected because an admin changed the sign-in method or MFA enforcement. */
export function isAuthConfigChangedError(error: unknown): boolean {
  return (error as HttpErrorResponse)?.error?.code === 'AUTH_CONFIG_CHANGED';
}

function isLoginChallengeResponse(response: LoginApiResponse): response is LoginChallengeResponse {
  return (
    response.isAuthenticated === false &&
    typeof (response as LoginChallengeResponse).challenge === 'string' &&
    typeof (response as LoginChallengeResponse).session === 'string'
  );
}

/**
 * Payload for POST /auth/login/ — credentials or Cognito challenge completion.
 * In EMAIL_OTP mode the first request carries only `email`.
 */
export interface LoginRequest {
  email: string;
  password?: string;
  session?: string;
  challenge?: string;
  mfa_code?: string;
  new_password?: string;
  /** EMAIL_OTP code, or the authenticator code that confirms MFA_SETUP. */
  code?: string;
  deviceName?: string;
}

export interface MfaStatus {
  totpEnabled: boolean;
  smsEnabled: boolean;
  preferredMfa: string | null;
  userMFASettingList: string[];
  mfaRequiredByPool: boolean;
  mfaEnforced?: boolean;
  authMethod?: AuthMethod;
}

export interface MfaTotpSetupResponse {
  secretCode: string;
  otpauthUrl: string;
  session?: string;
  message?: string;
}

export interface MfaTotpVerifyRequest {
  code: string;
  deviceName?: string;
}

export interface MfaActionResponse {
  success: boolean;
  message?: string;
  preferredMfa?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TfService implements OnDestroy {
  public host = environment.apiUrlHost;
  user = User;
  langset: any;
  // Auth state management
  private authInfoSubject = new BehaviorSubject<AuthInfo>({ isAuthenticated: false });
  public authInfo$ = this.authInfoSubject.asObservable();
  // profile picture
  private pictureUrlSource = new BehaviorSubject<string | null>(null);
  pictureUrl$ = this.pictureUrlSource.asObservable();
  // router: any;
  private localStorageKey = 'isToggled';
  private isToggledSubject: BehaviorSubject<boolean>;
  public isToggled$;
  /** Proactive cookie refresh while the session is active (~15 min; access token ~20 min). */
  private readonly REFRESH_INTERVAL_MS = 15 * 60 * 1000;
  private refreshIntervalId: ReturnType<typeof setInterval> | null = null;
  constructor(
    private httpClient: HttpClient,
    public translate: TranslateService,
    private router: Router,
    private paginationService: PaginationService,
    private notificationService: NotificationService,
    private portalLayoutResolve: PortalLayoutResolveService,
    private cfHomeContent: CfHomeContentService,
    private productCardDisplayConfig: ProductCardDisplayConfigService,
    private filterLayoutConfig: FilterLayoutConfigService,
    private shoppingBasketLayoutConfig: ShoppingBasketLayoutConfigService,
  ) {
    const savedValue = localStorage.getItem(this.localStorageKey);
    const initialValue = savedValue !== null ? JSON.parse(savedValue) : false;
    this.isToggledSubject = new BehaviorSubject<boolean>(initialValue);
    this.isToggled$ = this.isToggledSubject.asObservable();
    // Check initial auth state
    this.checkAuthStatus().subscribe();
  }

  public checkAuthStatus(): Observable<AuthInfo> {
    return this.httpClient.get<AuthInfo>(`${this.host}/auth/status`, { withCredentials: true })
      .pipe(
        map((authInfo) => {
          console.log('Auth status from API:', authInfo);
          const ext = authInfo as AuthInfo & { groupName?: unknown };
          const normalized: AuthInfo = {
            ...authInfo,
            userGroups: this.normalizeUserGroups(ext.userGroups ?? ext.groupName),
          };
          return normalized;
        }),
        tap((normalized) => {
          const wasAuthenticated = this.authInfoSubject.getValue().isAuthenticated;
          this.authInfoSubject.next(normalized);
          this.portalLayoutResolve.setSessionAuthenticated(!!normalized.isAuthenticated);
          if (normalized.isAuthenticated && normalized.userGroups?.length) {
            localStorage.setItem('userGroup', JSON.stringify(normalized.userGroups));
          }
          console.log('Auth status:', this.authInfoSubject);
          if (normalized.isAuthenticated) {
            this.startProactiveRefresh();
            this.notificationService.initializeNotificationSystem();
            this.bootstrapPortalLayoutsIfNeeded(normalized.userGroups);
          } else {
            this.stopProactiveRefresh();
            this.notificationService.disconnect();
            // Only clear layout cache when transitioning from logged-in → guest.
            // Clearing on every unauthenticated check races with public /home geo lookup.
            if (wasAuthenticated) {
              this.portalLayoutResolve.clearCache();
              this.productCardDisplayConfig.clearCache();
              this.filterLayoutConfig.clearCache();
              this.shoppingBasketLayoutConfig.clearCache();
            }
          }
        }),
        catchError(error => {
          console.log('Auth check failed:', error);
          this.stopProactiveRefresh();
          this.authInfoSubject.next({ isAuthenticated: false });
          this.portalLayoutResolve.setSessionAuthenticated(false);
          this.notificationService.disconnect();
          return of({ isAuthenticated: false });
        })
      );
  }

  public getCurrentAuthInfo(): AuthInfo {
    // This is important - we need to directly access the value property
    const rawValue = this.authInfoSubject.getValue();
    console.log('Current auth info from BehaviorSubject:', rawValue);

    // Add a simple validation check
    if (rawValue && typeof rawValue === 'object') {
      return rawValue;
    } else {
      // Return a default value if something's wrong
      return { isAuthenticated: false };
    }
  }
  public initAuth(): Observable<AuthInfo> {
    return this.checkAuthStatus();
  }

  /** Resolve portal layouts for the logged-in customer (BP → country → general). */
  bootstrapPortalLayoutsIfNeeded(userGroups?: string[] | null): void {
    if (!shouldLoadPortalLayoutsForUser(userGroups)) {
      return;
    }
    // Homepage layout requires country_code. Detect it from the visitor IP, same as public /home.
    this.portalLayoutResolve.loadForSession([...PORTAL_COMPONENT_KEYS], undefined).subscribe({
      next: (data) => {
        console.log('[PortalLayoutResolve] resolve-batch loaded after login', data);
        this.productCardDisplayConfig.syncFromPortalCache();
        this.filterLayoutConfig.syncFromPortalCache();
      },
      error: (err) => {
        console.warn('[PortalLayoutResolve] resolve-batch failed after login', err);
      },
    });
  }

  public getUserFromLocalCache(): { [key: string]: any } | null {
    // eslint-disable-next-line no-undef
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        return JSON.parse(userData) as { [key: string]: any };
      } catch (error) {
        return null;
      }
    }
    return null;
  }

  updatePictureUrl(newUrl: string) {
    this.pictureUrlSource.next(newUrl);
  }
  public profile(userId: string): Observable<any> {
    return this.httpClient.get(`${this.host}/auth/profile/${userId}`)
  }
  public updateUser(userId: string, userData: any): Observable<any | HttpErrorResponse> {
    return this.httpClient.put<any>(`${this.host}/auth/profile/${userId}`, userData)
  }

  public signIn(credentials: LoginRequest): Observable<AuthInfo> {
    console.log(`Sending request to: ${this.host}/auth/login/`, credentials);

    return this.httpClient.post<LoginApiResponse>(
      `${this.host}/auth/login/`,
      credentials,
      {
        withCredentials: true,
        // Auth pages use delayed app-please-wait; skip global spinner overlay
        context: new HttpContext().set(SKIP_SPINNER, true),
      }
    ).pipe(
      switchMap((response) => {
        if (isLoginChallengeResponse(response)) {
          return of({
            isAuthenticated: false,
            email: response.email ?? credentials.email,
            challenge: response.challenge,
            session: response.session,
            secretCode: response.secretCode,
            otpauthUrl: response.otpauthUrl ?? undefined,
          } as AuthInfo);
        }

        const authInfo: AuthInfo = {
          isAuthenticated: true,
          username: response.username,
          email: credentials.email,
          isStaff: response.is_staff,
          userGroups: this.normalizeUserGroups(response.groupName),
          userId: response.id,
        };

        this.authInfoSubject.next(authInfo);
        // Confirm cookies via /auth/status before session-scoped layout resolve runs.
        return this.checkAuthStatus().pipe(
          map((status) => (status.isAuthenticated ? status : authInfo)),
        );
      }),
      catchError((error) => {
        console.error("Login failed:", error);
        this.authInfoSubject.next({ isAuthenticated: false });
        this.portalLayoutResolve.setSessionAuthenticated(false);
        // Preserve original HttpErrorResponse so callers can detect network / 5xx
        return throwError(() => error);
      })
    );
  }

  public logout(): Observable<any> {
    return this.httpClient
      .post(`${this.host}/auth/logout`, {}, { withCredentials: true })
      .pipe(
        tap(() => {
          this.stopProactiveRefresh();
          // Reset authentication state
          this.authInfoSubject.next({
            isAuthenticated: false,
            username: undefined,
            isStaff: undefined,
            userGroups: undefined,
            userId: undefined,
          });

          // Clear storage
          localStorage.removeItem('user');
          localStorage.clear();

          this.notificationService.disconnect();
          this.portalLayoutResolve.setSessionAuthenticated(false);
          this.portalLayoutResolve.clearCache();
          this.cfHomeContent.clearCache();
          this.productCardDisplayConfig.clearCache();
          this.filterLayoutConfig.clearCache();
          this.shoppingBasketLayoutConfig.clearCache();
          this.setupLanguageSettings();
        }),
        catchError((error) => {
          console.error('Logout failed', error);

          // Reset authentication state on error
          this.authInfoSubject.next({ isAuthenticated: false });
          localStorage.clear();
          this.portalLayoutResolve.setSessionAuthenticated(false);
          this.portalLayoutResolve.clearCache();
          this.cfHomeContent.clearCache();
          this.productCardDisplayConfig.clearCache();
          this.filterLayoutConfig.clearCache();
          this.shoppingBasketLayoutConfig.clearCache();
          this.notificationService.disconnect();

          // Rethrow the error to be handled by the caller
          return throwError(() => error);
        })
      );
  }
  public addUserToLocalCache(user: User): void {
    localStorage.setItem('user', JSON.stringify(user));
  }
  private setupLanguageSettings() {
    this.langset = this.translate.getBrowserLang();
    localStorage.setItem("lang", this.langset);
    if (!(this.langset in ["en", "De", "fr"])) {
      this.translate.setDefaultLang('en');
      this.translate.use('en');
    }
  }
  public changePassword(userData: Record<string, any>): Observable<any | HttpErrorResponse> {
    console.log("userData", userData)
    return this.httpClient.patch<any>(
      `${this.host}/auth/UserPasswordChange/`,
      userData,
      { withCredentials: true }
    );
  }

  public getMfaStatus(): Observable<MfaStatus> {
    return this.httpClient.get<MfaStatus>(`${this.host}/auth/mfa/status/`, { withCredentials: true });
  }

  public setupMfaTotp(): Observable<MfaTotpSetupResponse> {
    return this.httpClient.post<MfaTotpSetupResponse>(
      `${this.host}/auth/mfa/totp/setup/`,
      {},
      { withCredentials: true }
    );
  }

  public verifyMfaTotp(payload: MfaTotpVerifyRequest): Observable<MfaActionResponse> {
    return this.httpClient.post<MfaActionResponse>(
      `${this.host}/auth/mfa/totp/verify/`,
      payload,
      { withCredentials: true }
    );
  }

  public disableMfaTotp(): Observable<MfaActionResponse> {
    return this.httpClient.post<MfaActionResponse>(
      `${this.host}/auth/mfa/totp/disable/`,
      {},
      { withCredentials: true }
    );
  }
  public register(register: RegisterModel): Observable<HttpResponse<any> | HttpErrorResponse> {
    return this.httpClient.post<any>(`${this.host}/auth/user-requests/`, register, {
      observe: 'response',
      context: new HttpContext().set(SKIP_SPINNER, true),
    });
  }

  public b2cRegister(register: RegisterModel): Observable<HttpResponse<any> | HttpErrorResponse> {
    return this.httpClient.post<any>(`${this.host}/auth/b2c-signup/`, register, {
      observe: 'response',
      context: new HttpContext().set(SKIP_SPINNER, true),
    });
  }

  public listRequestUsers(query): Observable<any> {
    const query1 = new HttpParams().set('query', query);
    return this.httpClient.get(`${this.host}/auth/user-requests/`, { params: query1 })
  }



  public updateStatusUser(id: any, status: any): Observable<any | HttpErrorResponse> {
    return this.httpClient.put<any>(`${this.host}/auth/user-requests/${id}`, status)
  }
  updateStatusUserInvitation(
    userRequestId: number,
    status: string,
    selectedBpName: string
  ): Observable<any> {
    return this.httpClient.put(
      `${this.host}/auth/user-requests/${userRequestId}`,
      {
        status: status,
        customer: selectedBpName // send the updated customer
      },
      { headers: { 'Content-Type': 'application/json' } }
    );
  }



  public deleteRequestUser(id: any): Observable<any> {
    return this.httpClient.delete<any>(`${this.host}/auth/user-requests/${id}`)
  }

  public reset(email: { email: string }): Observable<HttpResponse<any> | HttpErrorResponse> {
    return this.httpClient.post<any>(`${this.host}/auth/reset`, email, {
      observe: 'response',
      withCredentials: true,
      context: new HttpContext().set(SKIP_SPINNER, true),
    });
  }

  public completeReset(data: Record<string, unknown>): Observable<HttpResponse<any> | HttpErrorResponse> {
    return this.httpClient.patch<any>(`${this.host}/auth/password-reset-complete/`, data, {
      observe: 'response',
      withCredentials: true,
      context: new HttpContext().set(SKIP_SPINNER, true),
    });
  }
  public currentUserlogged(): string {
    let userId = '';
    this.authInfo$.subscribe(authInfo => {
      userId = authInfo.userId || '';
    }).unsubscribe();
    return userId;
  }

  public isAdmin(): Observable<boolean> {
    console.log("isAdmin");
    return this.authInfo$.pipe(
      map(authInfo => authInfo.userGroups?.includes('admin') || false)
    )
  }

  public getUserGroups(): Observable<string[]> {
    return this.authInfo$.pipe(
      map(authInfo => this.normalizeUserGroups(authInfo.userGroups))
    );
  }

  /**
   * Coerces API / storage shapes into a string[] (e.g. single group string vs array).
   * Prevents sidebar treating admins as contacts when `groupName` is not an array.
   */
  normalizeUserGroups(value: unknown): string[] {
    if (value == null) return [];
    if (Array.isArray(value)) {
      return value.flatMap((g) => (typeof g === 'string' && g.trim() ? [g.trim()] : []));
    }
    if (typeof value === 'string') {
      const t = value.trim();
      return t ? [t] : [];
    }
    return [];
  }

  /**
   * Users who share the customer portal shell and `role: 'contact'` nav items (permission codes, menu).
   * `adminCustomer` alone must still load contact-side permissions so gated modules appear.
   */
  isCustomerPortalUser(groups: string[] | undefined | null): boolean {
    const g = this.normalizeUserGroups(groups);
    return g.includes('contact') || g.includes('adminCustomer') || g.includes('customer');
  }

  /** Customer-portal users in the `adminCustomer` group (catalog / BP / pricing UI, stricter tabs). */
  isAdminCustomer(): boolean {
    return this.getUserGroupsFromLocalStorage().includes('adminCustomer');
  }

  /** Restricted customer admin: `adminCustomer` without full staff `admin` group. */
  isAdminCustomerOnly(): boolean {
    const g = this.getUserGroupsFromLocalStorage();
    return g.includes('adminCustomer') && !g.includes('admin');
  }

  /**
   * URL prefixes under `/admin` that `adminCustomer` may open. Keep in sync with their sidebar links in `navigation.ts`.
   */
  isAdminCustomerAllowedAdminUrl(url: string): boolean {
    const prefixes = [
      '/admin/cf-categorie',
      '/admin/cf-product',
      '/admin/cp-bp-management',
      '/admin/cp-pricing',
    ];
    return prefixes.some((p) => url.startsWith(p));
  }

  /** Parses `userGroup` from localStorage (set at login). */
  getUserGroupsFromLocalStorage(): string[] {
    const raw = localStorage.getItem('userGroup');
    if (!raw) return [];
    try {
      return this.normalizeUserGroups(JSON.parse(raw));
    } catch {
      return [];
    }
  }

  /**
   * Default home route for customer-portal users after login.
   * `adminCustomer` users get the KPI overview; `contact` / `group_contact` users get CfDashboard (orders, calendar).
   */
  getDefaultCustomerDashboardUrlForGroups(userGroups: string[] | undefined | null): string {
    const groups = this.normalizeUserGroups(userGroups);
    if (groups.includes('adminCustomer')) {
      return '/customer/CFDashboard/admin-customer-dashboard';
    }
    return '/customer/CFDashboard/dashboard';
  }

  getDefaultCustomerDashboardUrl(): string {
    const auth = this.getCurrentAuthInfo();
    const fromAuth = this.normalizeUserGroups(auth.userGroups);
    if (fromAuth.length) {
      return this.getDefaultCustomerDashboardUrlForGroups(fromAuth);
    }
    return this.getDefaultCustomerDashboardUrlForGroups(this.getUserGroupsFromLocalStorage());
  }

  public isStaff(): Observable<boolean> {
    return this.authInfo$.pipe(
      map(authInfo => authInfo.isStaff || false)
    );
  }

  public isLoggedIn(): boolean {
    const authInfo = this.getCurrentAuthInfo();
    const authStatus = authInfo?.isAuthenticated || false;
    console.log('isLoggedIn check:', authStatus);
    return authStatus;
  }

  refreshAccessToken(): Observable<any> {
    const authInfo = this.getCurrentAuthInfo();
    const body = authInfo.email ? { email: authInfo.email } : {};

    return this.httpClient.post<any>(
      `${this.host}/auth/refresh/`,
      body,
      {
        withCredentials: true,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      }
    ).pipe(
      tap(() => this.checkAuthStatus()),
      catchError((error: HttpErrorResponse) => {
        if (isAuthConfigChangedError(error)) {
          this.stopProactiveRefresh();
          this.logout().subscribe({ error: () => {} });
          this.router.navigate(['/auth/signin'], {
            queryParams: { reason: AUTH_CONFIG_CHANGED_REASON },
          });
        } else if (error.status === 401 || error.status === 403) {
          this.stopProactiveRefresh();
          this.logout().subscribe({ error: () => {} });
        }
        return throwError(() => error);
      })
    );
  }

  private startProactiveRefresh(): void {
    this.stopProactiveRefresh();
    this.refreshIntervalId = setInterval(() => {
      if (this.getCurrentAuthInfo().isAuthenticated) {
        this.refreshAccessToken().subscribe({ error: () => {} });
      } else {
        this.stopProactiveRefresh();
      }
    }, this.REFRESH_INTERVAL_MS);
  }

  private stopProactiveRefresh(): void {
    if (this.refreshIntervalId !== null) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
  }

  ngOnDestroy(): void {
    this.stopProactiveRefresh();
  }

  public listUsers(query,group,params: PaginationParams = {}): Observable<any> {
    const searchParams: PaginationParams = {
          ...params,
          query: query,
          group: group
        }
    return this.paginationService.getPaginatedData(`${this.host}/auth/UsersList`, searchParams)
  }

  public createUser(userData: any): Observable<any | HttpErrorResponse> {
    console.log("userData", userData)
    return this.httpClient.post<any>(`${this.host}/auth/regiterUser/`, userData)
  }

  public deleteUser(data: { email: string }): Observable<any> {
    return this.httpClient.delete<any>(`${this.host}/auth/UsersDelete`, { body: data })
  }

  public deleteInviationUser(userId: number): Observable<any> {
    return this.httpClient.delete<any>(`${this.host}/auth/UsersInvitationDelete/${userId}`);
  }


}
